<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowRight, BookOpen, BookOpenCheck, Headphones, Info, PenLine, RotateCcw, Sparkles, Tags } from '@lucide/vue'
import PageHeader from '../components/PageHeader.vue'
import GoalPicker from '../components/GoalPicker.vue'
import { api, post } from '../api'
import { session } from '../state'
import { isEnglish } from '../i18n'

const router = useRouter()
const data = ref(null)
const goal = ref({ mode: 'standard', new_words: 15, review_limit: 40, dictation: 10 })
const error = ref('')
const busy = ref(false)
const resetBusy = ref(false)
const resetError = ref('')
const saveDefault = ref(false)
const l = computed(() => isEnglish.value ? {
  header: `${session.user.display_name}, one step forward today`, subtitle: 'Complete the locked core plan to check in. Accuracy only changes later review.',
  loading: 'Preparing today’s cards…', locked: 'Today’s plan is locked', watered: 'Your garden is watered for today', continuePlan: 'Keep learning today',
  new: 'new', review: 'review', dictation: 'dictation', backlog: 'more due words will be scheduled tomorrow.', practiceMore: 'Extra practice', continue: 'Continue learning',
  route: 'Today’s route', routeTitle: 'From recognition to recall', preview: 'Preview words', recognized: 'new words previewed', firstLook: 'new words to preview',
  meaning: 'Meaning recognition', meaningDetail: 'Build a first impression', spelling: 'Active spelling', spellingDetail: 'Recall German from English', gender: 'Noun gender', genderDetail: 'Tell der, die and das apart', listening: 'Dictation', listeningDetail: 'Hear and write accurately',
  pace: 'Choose your pace before you start', howMuch: 'How much would you like to learn today?', lockedDetail: 'Your plan locks when you start; add practice whenever you have energy.', save: 'Save as my future default',
  duePrefix: 'There are', dueMiddle: 'due words; the', dueSuffix: 'most urgent will be scheduled first.', backlogAdvice: 'reviews are waiting. Consider fewer new words today.', scheduling: 'Planning…', start: 'Start today’s learning', quote: 'Practice makes progress, one small step at a time.', serverError: 'The service could not create today’s plan. Please try again shortly.',
  resetTitle: 'Testing: restart today', resetText: 'Your existing review history, FSRS and check-in stay intact. You can reset as often as you need.', regenerate: 'Use a new set of questions', replay: 'Reset this set', resetting: 'Resetting…',
} : {
  header: `${session.user.display_name}，今天也向前一点`, subtitle: '完成锁定任务即可打卡，正确率只影响复习安排。', loading: '正在整理今天的词卡…', locked: '今日任务已锁定', watered: '今日花园已经浇水', continuePlan: '继续完成今天的学习', new: '新词', review: '复习', dictation: '听写', backlog: '个到期词，明天继续安排。', practiceMore: '再练一组', continue: '继续学习', route: '今日路线', routeTitle: '从认得，到写得出来', preview: '学前词表', recognized: '已认识', firstLook: '先认识', meaning: '词义识别', meaningDetail: '建立第一印象', spelling: '主动拼写', spellingDetail: '从中文回忆德语', gender: '名词词性', genderDetail: '区分 der、die、das', listening: '发音听写', listeningDetail: '听懂并准确写出', pace: '在开始前决定节奏', howMuch: '今天想学多少？', lockedDetail: '点击开始后任务会锁定；学有余力时可以随时追加练习。', save: '保存为今后的默认学习量', duePrefix: '有', dueMiddle: '个到期词，将先安排最需要复习的', dueSuffix: '个。', backlogAdvice: '个复习积压，建议先消化复习、今天少学新词。', scheduling: '正在排课…', start: '开始今日学习', quote: '熟能生巧，但每次只需做好今天这一小步。', serverError: '创建今日任务时服务暂时出错，请稍后重试。',
  resetTitle: '测试工具：重新开始今日学习', resetText: '已保留的复习记录、FSRS 与打卡不会改变；可按需要反复重制。', regenerate: '换一组题', replay: '仅重置当前题', resetting: '正在重制…',
})

async function load() {
  try {
    data.value = await api('/api/today')
    if (!data.value.locked) goal.value = { ...data.value.goal }
  } catch (e) { error.value = e.message }
}
async function start() {
  busy.value = true; error.value = ''
  try {
    await post('/api/today/start', { ...goal.value, save_default: saveDefault.value })
    router.push('/practice')
  } catch (e) {
    // Never expose an unhelpful upstream status string to learners. Specific
    // validation errors still come from the API unchanged.
    error.value = e.message === 'Internal Server Error'
      ? l.value.serverError
      : e.message
  } finally { busy.value = false }
}
async function resetToday(mode) {
  const changing = mode === 'regenerate'
  if (!window.confirm(changing ? '换一组今天的题目？原有学习记录、FSRS 与打卡会保留。' : '仅重置当前题目的完成进度？')) return
  resetBusy.value = true; resetError.value = ''
  try { await post('/api/today/reset', { mode }); router.push('/practice') }
  catch (e) { resetError.value = e.message }
  finally { resetBusy.value = false }
}
onMounted(load)
</script>

<template>
  <div>
    <PageHeader eyebrow="HEUTE" :title="l.header" :subtitle="l.subtitle">
      <RouterLink class="icon-button" to="/story" aria-label="生成 AI 短文"><Sparkles :size="21" /></RouterLink>
    </PageHeader>
    <div v-if="!data" class="loading-card">{{l.loading}}</div>
    <template v-else-if="data.locked">
      <section class="hero-card today-locked">
        <div class="progress-ring" :style="{ '--progress': `${Math.round((data.plan.completed_items / Math.max(1,data.plan.total_items))*360)}deg` }"><span><b>{{ data.plan.completed_items }}</b><small>/ {{ data.plan.total_items }}</small></span></div>
        <div><p class="eyebrow">{{l.locked}}</p><h2>{{ data.plan.status === 'completed' ? l.watered : l.continuePlan }}</h2><p>{{ data.plan.new_target }} {{l.new}} · {{ data.plan.review_target }} {{l.review}} · {{ data.plan.dictation_target }} {{l.dictation}}</p><p v-if="data.plan.backlog_count" class="backlog">{{ data.plan.backlog_count }} {{l.backlog}}</p></div>
        <button class="primary-button" @click="router.push('/practice')">{{ data.plan.status === 'completed' ? l.practiceMore : l.continue }} <ArrowRight :size="18" /></button>
      </section>
      <section v-if="session.user?.can_reset_today" class="section-card today-reset-card"><div><p class="eyebrow">TESTING</p><h2>{{l.resetTitle}}</h2><p>{{l.resetText}}</p></div><div class="today-reset-actions"><button class="primary-button" :disabled="resetBusy" @click="resetToday('regenerate')"><Sparkles :size="17" />{{resetBusy?l.resetting:l.regenerate}}</button><button class="secondary-button" :disabled="resetBusy" @click="resetToday('replay')"><RotateCcw :size="17" />{{l.replay}}</button></div><p v-if="resetError" class="form-error">{{resetError}}</p></section>
      <section class="section-card">
        <div class="section-title"><div><p class="eyebrow">{{l.route}}</p><h2>{{l.routeTitle}}</h2></div></div>
        <div class="route-list">
          <div v-if="data.study_words?.length" :class="{ 'route-done': data.study_completed }"><span class="route-icon"><BookOpen /></span><b>{{l.preview}}</b><small>{{ data.study_completed ? `✓ ${data.study_words.length} ${l.recognized}` : `${data.study_words.length} ${l.firstLook}` }}</small></div>
          <div v-if="data.question_types?.meaning"><span class="route-icon"><BookOpenCheck /></span><b>{{l.meaning}}</b><small>{{l.meaningDetail}}</small></div>
          <div v-if="data.question_types?.spelling"><span class="route-icon"><PenLine /></span><b>{{l.spelling}}</b><small>{{l.spellingDetail}}</small></div>
          <div v-if="data.question_types?.article || data.question_types?.plural"><span class="route-icon"><Tags /></span><b>{{l.gender}}</b><small>{{l.genderDetail}}</small></div>
          <div v-if="data.question_types?.dictation"><span class="route-icon"><Headphones /></span><b>{{l.listening}}</b><small>{{l.listeningDetail}}</small></div>
        </div>
      </section>
    </template>
    <template v-else>
      <section class="hero-card start-card">
        <div class="start-copy"><p class="eyebrow">{{l.pace}}</p><h2>{{l.howMuch}}</h2><p>{{l.lockedDetail}}</p></div>
        <GoalPicker v-model="goal" />
        <label class="check-row"><input v-model="saveDefault" type="checkbox" /><span>{{l.save}}</span></label>
        <p v-if="data.available?.due > goal.review_limit" class="backlog">{{l.duePrefix}} {{ data.available.due }} {{l.dueMiddle}} {{ goal.review_limit }} {{l.dueSuffix}}</p>
        <p v-if="error" class="form-error">{{ error }}</p>
        <p v-if="data.backlog_pause_suggested" class="pause-chip"><Info :size="15" /><span>{{ data.backlog }} {{l.backlogAdvice}}</span></p>
        <button class="primary-button large" :disabled="busy" @click="start">{{ busy ? l.scheduling : l.start }} <ArrowRight :size="19" /></button>
      </section>
      <section class="quote-card"><p>“Übung macht den Meister.”</p><span>{{l.quote}}</span></section>
    </template>
  </div>
</template>

<style scoped>
/* —— Stage 3 复查：720px App 壳下的密度与硬编码修正（script/模板未动） —— */

/* 桌面视口下全局默认 4 列路线在 720px 壳内过挤，统一为双列；
   ≤900px 视口沿用全局响应式（双列→单列），此处不干预 */
@media (min-width: 901px) {
  .route-list { grid-template-columns: 1fr 1fr; }
}

/* 锁定态 hero：三列（环+文+钮）在 720px 壳内局促，主按钮独占整行；
   ≤600px 视口沿用全局单列居中堆叠，故仅 ≥601px 生效 */
@media (min-width: 601px) {
  .today-locked { grid-template-columns: auto 1fr; }
  .today-locked > .primary-button { grid-column: 1 / -1; }
}

/* 进度环内盘：写死的 var(--paper) 实底 → 浮起玻璃碟，暗色经令牌自动翻转 */
.progress-ring::before {
  background: var(--glass-raised-bg);
  box-shadow: var(--glass-raised-shadow);
}

/* 勾选行：补足 44px 触控目标，玻璃 inset 行（免 blur） */
.check-row {
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  background: var(--glass-bg-inset);
}

/* 路线卡：统一最小行高，双列下视觉更齐整 */
.route-list > div { min-height: 118px; align-content: start; }

/* 「学前词表」完成态：图标翻转为实心 sage，不打乱网格 */
.route-done .route-icon { background: var(--sage); color: var(--on-accent); }

/* 积压保护提示条：玻璃 chip，温和非阻断 */
.pause-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted);
  background: var(--glass-bg-inset);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
}
.pause-chip > svg { flex: none; color: var(--sage); }
.today-reset-card{display:grid;gap:13px;margin-top:16px}.today-reset-card h2{margin:3px 0 5px;font-size:20px}.today-reset-card p:not(.eyebrow):not(.form-error){margin:0;color:var(--muted);line-height:1.55}.today-reset-actions{display:flex;gap:9px;flex-wrap:wrap}.today-reset-actions button{flex:1;min-width:150px;justify-content:center}
@media (max-width:420px){.today-reset-actions{display:grid;grid-template-columns:1fr}.today-reset-actions button{width:100%}}
</style>
